import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FileQuestion,
  Plus,
  MoreVertical,
  Edit,
  Loader2,
  Trash2,
  ArrowLeft,
  Code,
  MessageSquare,
  CheckSquare,
  Upload,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface EvalItem {
  id: string;
  itemId: string;
  type: string;
  section: string | null;
  prompt: string;
  options: any;
  outputSchema: any;
  correct: any;
  goldenKeywords: string[];
  points: number;
  timeBudgetSec: number;
  order: number;
}

const isValidJson = (str: string): boolean => {
  if (!str.trim()) return true;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

const tryFormat = (str: string): string => {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
};

const ITEM_TYPES = [
  { value: "MCQ", label: "Multiple Choice", icon: CheckSquare },
  { value: "SHORT_ANSWER", label: "Short Answer", icon: MessageSquare },
  { value: "STRUCTURED_TASK", label: "Structured Task", icon: FileQuestion },
  { value: "CODE_TASK", label: "Code Task", icon: Code },
  { value: "API_TASK", label: "API Task", icon: FileQuestion },
  { value: "SQL_TASK", label: "SQL Task", icon: FileQuestion },
];

export default function EvalItemsManagement() {
  const { packId } = useParams<{ packId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EvalItem | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    itemId: "",
    type: "MCQ",
    section: "",
    prompt: "",
    options: "{}",
    outputSchema: "{}",
    correct: "{}",
    goldenKeywords: "",
    points: "1",
    timeBudgetSec: "60",
    order: "",
  });

  // Fetch pack details
  const { data: packResponse } = useQuery({
    queryKey: ["admin", "eval-pack", packId],
    queryFn: async () => {
      const res = await api.get(`/admin/eval-packs/${packId}`);
      return res.data;
    },
    enabled: !!packId,
  });

  const pack = packResponse?.data;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.post(`/admin/eval-packs/${packId}/items`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-pack", packId] });
      toast.success("Quiz item created successfully");
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to create item");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: any }) => {
      const res = await api.patch(`/admin/eval-packs/${packId}/items/${itemId}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-pack", packId] });
      toast.success("Quiz item updated successfully");
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update item");
    },
  });

  // Replace items mutation
  const replaceMutation = useMutation({
    mutationFn: async (items: any[]) => {
      const res = await api.put(`/admin/eval-packs/${packId}/items`, { items });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-pack", packId] });
      toast.success("Items replaced successfully");
      setImportOpen(false);
      setImportJson("");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to replace items");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const res = await api.delete(`/admin/eval-packs/${packId}/items/${itemId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "eval-pack", packId] });
      toast.success("Quiz item deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete item");
    },
  });

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      itemId: "",
      type: "MCQ",
      section: "",
      prompt: "",
      options: JSON.stringify({
        choices: [
          { id: "a", text: "Option A" },
          { id: "b", text: "Option B" },
        ],
      }, null, 2),
      outputSchema: "{}",
      correct: JSON.stringify({ optionId: "a" }, null, 2),
      goldenKeywords: "",
      points: "1",
      timeBudgetSec: "60",
      order: "",
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (item: EvalItem) => {
    setEditingItem(item);
    setFormData({
      itemId: item.itemId,
      type: item.type,
      section: item.section || "",
      prompt: item.prompt,
      options: item.options != null ? JSON.stringify(item.options, null, 2) : "{}",
      outputSchema: item.outputSchema != null ? JSON.stringify(item.outputSchema, null, 2) : "{}",
      correct: item.correct != null ? JSON.stringify(item.correct, null, 2) : "{}",
      goldenKeywords: item.goldenKeywords.join(", "),
      points: item.points.toString(),
      timeBudgetSec: item.timeBudgetSec.toString(),
      order: item.order.toString(),
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.itemId || !formData.prompt) {
      toast.error("Item ID and Prompt are required");
      return;
    }

    // Validate JSON fields
    let options, outputSchema, correct;
    try {
      options = formData.options ? JSON.parse(formData.options) : null;
      outputSchema = formData.outputSchema ? JSON.parse(formData.outputSchema) : null;
      correct = formData.correct ? JSON.parse(formData.correct) : null;
    } catch (e) {
      toast.error("Invalid JSON in options, outputSchema, or correct fields");
      return;
    }

    const payload = {
      itemId: formData.itemId,
      type: formData.type,
      section: formData.section || null,
      prompt: formData.prompt,
      options,
      outputSchema,
      correct,
      goldenKeywords: formData.goldenKeywords
        ? formData.goldenKeywords.split(",").map((k) => k.trim())
        : [],
      points: parseInt(formData.points),
      timeBudgetSec: parseInt(formData.timeBudgetSec),
      order: formData.order ? parseInt(formData.order) : undefined,
    };

    if (editingItem) {
      updateMutation.mutate({ itemId: editingItem.itemId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleImport = () => {
    let parsed: any;
    try {
      parsed = JSON.parse(importJson);
    } catch {
      toast.error("Invalid JSON");
      return;
    }
    const items = Array.isArray(parsed) ? parsed : [parsed];
    if (items.length === 0) {
      toast.error("No items found in JSON");
      return;
    }
    replaceMutation.mutate(items);
  };

  const handleDelete = (itemId: string) => {
    if (confirm("Are you sure you want to delete this item?")) {
      deleteMutation.mutate(itemId);
    }
  };

  const getItemIcon = (type: string) => {
    const itemType = ITEM_TYPES.find((t) => t.value === type);
    return itemType?.icon || FileQuestion;
  };

  const items = pack?.items || [];

  const jsonErrors = {
    options: !isValidJson(formData.options),
    outputSchema: !isValidJson(formData.outputSchema),
    correct: !isValidJson(formData.correct),
    import: !isValidJson(importJson),
  };
  const hasJsonError = jsonErrors.options || jsonErrors.outputSchema || jsonErrors.correct;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/quiz-packs")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">
            {pack?.title || "Quiz Items"}
          </h1>
          <p className="text-muted-foreground">
            {pack?.packId} • {items.length} item{items.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button variant="outline" onClick={() => { setImportJson(""); setImportOpen(true); }}>
          <Upload className="mr-2 h-4 w-4" />
          Import JSON
        </Button>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Item
        </Button>
      </div>

      {/* Items List */}
      <div className="space-y-3">
        {items.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileQuestion className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No items yet</p>
              <Button variant="link" onClick={handleCreate} className="mt-2">
                Add your first item
              </Button>
            </CardContent>
          </Card>
        ) : (
          items.map((item: EvalItem, index: number) => {
            const Icon = getItemIcon(item.type);
            return (
              <Card key={item.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="molt-icon-surface flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <Badge variant="secondary" className="text-xs">
                            {item.type}
                          </Badge>
                          {item.section && (
                            <Badge variant="outline" className="text-xs">
                              {item.section}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm line-clamp-2">{item.prompt}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Points: {item.points}</span>
                          <span>Time: {item.timeBudgetSec}s</span>
                          <span>ID: {item.itemId}</span>
                        </div>
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(item)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(item.itemId)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
              </Card>
            );
          })
        )}
      </div>

      {/* Import Items Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Import Items from JSON
              <button type="button" onClick={() => setImportJson(j => tryFormat(j))} disabled={jsonErrors.import || !importJson.trim()} className="flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed mr-4">
                <Wand2 className="h-3 w-3" />Format
              </button>
            </DialogTitle>
            <DialogDescription>
              Paste a JSON array of items. This will <strong>replace all existing items</strong> for this pack atomically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              className={`font-mono text-xs min-h-[300px] ${importJson.trim() && jsonErrors.import ? "border-destructive focus-visible:ring-destructive" : ""}`}
              placeholder={`[\n  {\n    "itemId": "item_01",\n    "type": "MCQ",\n    "section": "Basics",\n    "prompt": "What HTTP method is idempotent?",\n    "options": {"choices": [{"id": "a", "text": "GET"}, {"id": "b", "text": "POST"}]},\n    "correct": {"optionId": "a"},\n    "points": 2,\n    "timeBudgetSec": 60\n  }\n]`}
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
            />
            {importJson.trim() && jsonErrors.import && (
              <p className="text-xs text-destructive mt-1">Invalid JSON</p>
            )}
            <p className="text-xs text-muted-foreground">
              Required fields per item: <code>itemId</code>, <code>type</code>, <code>prompt</code>. Optional: <code>section</code>, <code>options</code>, <code>correct</code>, <code>outputSchema</code>, <code>goldenKeywords</code>, <code>points</code>, <code>timeBudgetSec</code>, <code>order</code>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={replaceMutation.isPending || !importJson.trim() || jsonErrors.import}
            >
              {replaceMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Replace Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Quiz Item" : "Create Quiz Item"}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? "Update item configuration" : "Add a new quiz item"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Item ID & Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="itemId">Item ID *</Label>
                <Input
                  id="itemId"
                  placeholder="item_01_01"
                  value={formData.itemId}
                  onChange={(e) =>
                    setFormData({ ...formData, itemId: e.target.value })
                  }
                  disabled={!!editingItem}
                />
              </div>

              <div>
                <Label htmlFor="type">Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ITEM_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Section */}
            <div>
              <Label htmlFor="section">Section (optional)</Label>
              <Input
                id="section"
                placeholder="Communication, Task Comprehension, etc."
                value={formData.section}
                onChange={(e) =>
                  setFormData({ ...formData, section: e.target.value })
                }
              />
            </div>

            {/* Prompt */}
            <div>
              <Label htmlFor="prompt">Prompt *</Label>
              <Textarea
                id="prompt"
                placeholder="What HTTP method is idempotent?"
                value={formData.prompt}
                onChange={(e) =>
                  setFormData({ ...formData, prompt: e.target.value })
                }
                rows={4}
              />
            </div>

            {/* Options (JSON) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="options">Options (JSON)</Label>
                <button type="button" onClick={() => setFormData(f => ({ ...f, options: tryFormat(f.options) }))} disabled={jsonErrors.options} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed">
                  <Wand2 className="h-3 w-3" />Format
                </button>
              </div>
              <Textarea
                id="options"
                placeholder='{"choices": [{"id": "a", "text": "..."}]}'
                value={formData.options}
                onChange={(e) =>
                  setFormData({ ...formData, options: e.target.value })
                }
                rows={6}
                className={`font-mono text-sm ${jsonErrors.options ? "border-destructive focus-visible:ring-destructive" : ""}`}
              />
              {jsonErrors.options ? (
                <p className="text-xs text-destructive mt-1">Invalid JSON</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  For MCQ: {`{"choices": [{"id": "a", "text": "Option A"}]}`}
                </p>
              )}
            </div>

            {/* Output Schema (JSON) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="outputSchema">Output Schema (JSON)</Label>
                <button type="button" onClick={() => setFormData(f => ({ ...f, outputSchema: tryFormat(f.outputSchema) }))} disabled={jsonErrors.outputSchema} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed">
                  <Wand2 className="h-3 w-3" />Format
                </button>
              </div>
              <Textarea
                id="outputSchema"
                placeholder='{"type": "object", "properties": {...}}'
                value={formData.outputSchema}
                onChange={(e) =>
                  setFormData({ ...formData, outputSchema: e.target.value })
                }
                rows={4}
                className={`font-mono text-sm ${jsonErrors.outputSchema ? "border-destructive focus-visible:ring-destructive" : ""}`}
              />
              {jsonErrors.outputSchema && (
                <p className="text-xs text-destructive mt-1">Invalid JSON</p>
              )}
            </div>

            {/* Correct Answer (JSON) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="correct">Correct Answer (JSON)</Label>
                <button type="button" onClick={() => setFormData(f => ({ ...f, correct: tryFormat(f.correct) }))} disabled={jsonErrors.correct} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed">
                  <Wand2 className="h-3 w-3" />Format
                </button>
              </div>
              <Textarea
                id="correct"
                placeholder='{"optionId": "a"} or {"keywords": [...]}'
                value={formData.correct}
                onChange={(e) =>
                  setFormData({ ...formData, correct: e.target.value })
                }
                rows={3}
                className={`font-mono text-sm ${jsonErrors.correct ? "border-destructive focus-visible:ring-destructive" : ""}`}
              />
              {jsonErrors.correct && (
                <p className="text-xs text-destructive mt-1">Invalid JSON</p>
              )}
            </div>

            {/* Golden Keywords */}
            <div>
              <Label htmlFor="goldenKeywords">Golden Keywords (comma-separated)</Label>
              <Input
                id="goldenKeywords"
                placeholder="rate limit, throttle, quota"
                value={formData.goldenKeywords}
                onChange={(e) =>
                  setFormData({ ...formData, goldenKeywords: e.target.value })
                }
              />
            </div>

            {/* Points, Time Budget, Order */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="points">Points</Label>
                <Input
                  id="points"
                  type="number"
                  min="1"
                  value={formData.points}
                  onChange={(e) =>
                    setFormData({ ...formData, points: e.target.value })
                  }
                />
              </div>

              <div>
                <Label htmlFor="timeBudgetSec">Time Budget (sec)</Label>
                <Input
                  id="timeBudgetSec"
                  type="number"
                  min="10"
                  value={formData.timeBudgetSec}
                  onChange={(e) =>
                    setFormData({ ...formData, timeBudgetSec: e.target.value })
                  }
                />
              </div>

              <div>
                <Label htmlFor="order">Order</Label>
                <Input
                  id="order"
                  type="number"
                  min="0"
                  placeholder="Auto"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending || hasJsonError}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingItem ? "Save Changes" : "Create Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
